import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  rate_limit_per_hour: number;
  expires_at: string | null;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface ApiKeysResponse {
  api_keys: ApiKey[];
}

interface ApiKeyResponse {
  api_key: ApiKey;
}

interface CreateApiKeyResponse {
  api_key: ApiKey;
  raw_key: string;
}

interface CreateApiKeyData {
  name: string;
  scopes?: string[];
  expires_at?: string;
  // ADV-998 — `'inbound'` mints an `adva_inb_*` key for the catch-all
  // inbound webhook endpoint; defaults to `'user'` on the backend.
  kind?: "user" | "inbound";
}

interface UpdateApiKeyData {
  name?: string;
  scopes?: string[];
  is_active?: boolean;
  rate_limit_per_hour?: number;
}

export function useApiKeys() {
  return useQuery({
    queryKey: ["api-keys"],
    queryFn: () => api.get<ApiKeysResponse>("/core/api-keys"),
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateApiKeyData) =>
      api.post<CreateApiKeyResponse>("/core/api-keys", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
}

export function useUpdateApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateApiKeyData) =>
      api.patch<ApiKeyResponse>(`/core/api-keys/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ message: string }>(`/core/api-keys/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });
}
